(function () {
  const FOOTER_SELECTOR = '.footer';
  const DEFAULT_PERSPECTIVE_PX = 1800;
  const DEFAULT_TILT_DEG = 58;
  const DEFAULT_SCALE = 1.45;
  /** Calibrated from prior desktop plane math (~H400/W1200). */
  const DEFAULT_LONG_BASE_ANGLE_DEG = 50;
  const MIN_EXCESS_PX = 1;
  const MIN_PERSPECTIVE_PX = 200;
  /** Cap W_bottom/W so d/P = r-1 ≤ 1 (CSS perspective stays projectable). */
  const MAX_WIDTH_RATIO = 2;

  /** @type {WeakMap<Element, ResizeObserver>} */
  const observers = new WeakMap();

  function parsePx(value, fallback) {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function parseDeg(value, fallback) {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  /**
   * Screen-space isosceles trapezoid with fixed long-base angles:
   * E = H / tan(θ_long), W_bottom = W + 2E, then r capped at MAX_WIDTH_RATIO.
   * Keep CSS tilt + scale; solve effective perspective P so projected top = W,
   * bottom = W_bottom, height = H.
   */
  function computeFooterFloorPlane(H_footer, W_footer, tiltDeg, S, longBaseDeg) {
    const tiltRad = (tiltDeg * Math.PI) / 180;
    const longBaseRad = (longBaseDeg * Math.PI) / 180;
    const sinT = Math.sin(tiltRad);
    const cosT = Math.cos(tiltRad);
    const tanLong = Math.tan(longBaseRad);
    const W = Math.max(1, W_footer);

    let E = tanLong > 0 ? H_footer / tanLong : H_footer;
    E = Math.max(MIN_EXCESS_PX, E);
    let r = (W + 2 * E) / W;
    if (r > MAX_WIDTH_RATIO) {
      r = MAX_WIDTH_RATIO;
      E = (W * (r - 1)) / 2;
    }
    const W_bottom = W + 2 * E;

    // P = H * r * tan(tilt) / (r - 1) — locks width ratio to 3D foreshortening.
    let P;
    if (r > 1.0001 && sinT > 0 && cosT > 0) {
      P = (H_footer * r * Math.tan(tiltRad)) / (r - 1);
    } else {
      P = DEFAULT_PERSPECTIVE_PX;
    }
    P = Math.max(MIN_PERSPECTIVE_PX, P);

    const k = 1 / r;
    const H_plane = cosT > 0 ? (H_footer * r) / (S * cosT) : H_footer / S;
    const W_plane = (W * r) / S;

    return {
      H_plane: Math.ceil(H_plane),
      W_plane: Math.ceil(W_plane),
      P: Math.round(P),
      E: Math.round(E),
      W_bottom: Math.round(W_bottom),
      k: k,
    };
  }

  function applyFooterFloorPlane(footer) {
    if (!(footer instanceof HTMLElement)) return;

    const styles = getComputedStyle(footer);
    const tiltDeg = parseDeg(styles.getPropertyValue('--footer-floor-tilt'), DEFAULT_TILT_DEG);
    const S = parsePx(styles.getPropertyValue('--footer-floor-scale'), DEFAULT_SCALE);
    const longBaseDeg = parseDeg(
      styles.getPropertyValue('--footer-floor-long-base-angle'),
      DEFAULT_LONG_BASE_ANGLE_DEG
    );
    const H_footer = footer.offsetHeight;
    const W_footer = footer.offsetWidth;

    if (H_footer <= 0 || W_footer <= 0) return;

    const { H_plane, W_plane, P, E, W_bottom } = computeFooterFloorPlane(
      H_footer,
      W_footer,
      tiltDeg,
      S,
      longBaseDeg
    );

    footer.style.setProperty('--footer-measured-height-px', H_footer + 'px');
    footer.style.setProperty('--footer-measured-width-px', W_footer + 'px');
    footer.style.setProperty('--footer-floor-perspective', P + 'px');
    footer.style.setProperty('--footer-floor-plane-height-px', H_plane + 'px');
    footer.style.setProperty('--footer-floor-plane-width-px', W_plane + 'px');
    footer.style.setProperty('--footer-floor-excess-px', E + 'px');
    footer.style.setProperty('--footer-floor-bottom-width-px', W_bottom + 'px');
  }

  function bindFooter(footer) {
    if (!(footer instanceof HTMLElement)) return;
    applyFooterFloorPlane(footer);

    if (typeof ResizeObserver === 'undefined') return;

    const existing = observers.get(footer);
    if (existing) {
      existing.disconnect();
    }

    const observer = new ResizeObserver(function () {
      applyFooterFloorPlane(footer);
    });
    observer.observe(footer);
    observers.set(footer, observer);
  }

  function initFooters(root) {
    root.querySelectorAll(FOOTER_SELECTOR).forEach(function (footer) {
      bindFooter(footer);
    });
  }

  function init() {
    initFooters(document);
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target instanceof HTMLElement) initFooters(event.target);
  });
})();
